import { useMsal } from "@azure/msal-react";
import { useGoogleLogin } from "@react-oauth/google";
import { useQuery } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { useState } from "react";
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { LoginConfigService } from "../api/LoginConfigService";
import { LanguagePicker } from "../components/LanguagePicker/LanguagePicker";
import { Button } from "../components/ui/Button/Button";
import { Card } from "../components/ui/Card/Card";
import { Link } from "../components/ui/Link/Link";
import { LoadingScreen } from "../components/ui/LoadingScreen";
import { Logo } from "../components/ui/Logo";
import { Paper } from "../components/ui/Paper/Paper";
import { Typography } from "../components/ui/Typography/Typography";
import { IconTypes } from "../components/ui/icons";
import { Icon } from "../components/ui/icons/Icon";
import { WarningIcon } from "../components/ui/icons/WarningIcon";
import { useAuth } from "../hooks/useAuth";
import { useOktaLogin } from "../hooks/useOktaLogin";
import useTranslation from "../hooks/useTranslation";
import User from "../models/user";
import {
  AuthClientProviderValue,
  AuthClientProviders,
  loginRequest,
} from "../providers/AuthProvider";
import Login from "../styles/illustrations/Login.gif";
import { ErrorResponse } from "../types/error";
import { LoginConfigResponse } from "../types/loginConfig";
import { IUser, InviteErrorCodes } from "../types/user";
import {
  TENANT_LOGIN_ID,
  USER,
  USER_INVITE_TOKEN_KEY,
} from "../utils/api.utils";
import { PublicRoutes, getLoginRoute } from "../utils/routes.utils";
import { getDefaultLoginRoute } from "../utils/subscription.utils";
import "./LoginPage.scss";

export const LoginPage = () => {
  const { t } = useTranslation("loginPage");
  const { onLogin, loadingAuth } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const loginConfigService = new LoginConfigService();
  const { tenantLoginId } = useParams();
  const [searchParams] = useSearchParams();
  const inviteToken = searchParams.get(USER_INVITE_TOKEN_KEY) || undefined;
  const [errMsg, setErrMsg] = useState<string>();

  const {
    refetch: fetchLoginConfig,
    isLoading: loadingLoginConfig,
    fetchStatus,
  } = useQuery<LoginConfigResponse, AxiosError>(
    ["loginConfig", tenantLoginId],
    () => loginConfigService.getLoginConfig(tenantLoginId || ""),
    { enabled: false, staleTime: 0, cacheTime: 0 }
  );

  const handleLogin = (
    provider: keyof AuthClientProviders,
    data: AuthClientProviderValue
  ) => {
    onLogin(provider, data)
      .then(() => {
        const userStorage = localStorage.getItem(USER);
        if (!userStorage) {
          navigate(getLoginRoute());
          return;
        }
        if (provider === "okta") {
          localStorage.setItem(TENANT_LOGIN_ID, tenantLoginId || "");
        }
        const userJSON: IUser = JSON.parse(userStorage);
        const user = new User(userJSON);
        // we always want to redirect the user to the signup page if company name is not set
        if (user.getCompanyName()) {
          const origin = getDefaultLoginRoute(
            user.company?.subscription_access_level ||
              user.company?.subscriptions,
            location.state?.from?.pathname
          );
          navigate(origin);
        } else {
          navigate(PublicRoutes.SIGNUP);
        }
      })
      .catch((error) => {
        const errorCode = error.response
          .data as ErrorResponse<InviteErrorCodes>;
        setErrMsg(getErrorMessage(errorCode));
      });
  };

  const getErrorMessage = (error: ErrorResponse<InviteErrorCodes>) => {
    if (error?.extra?.code === InviteErrorCodes.EMAIL_MISMATCH) {
      return t("errors.emailMismatch");
    } else if (error?.extra?.code === InviteErrorCodes.INVALID) {
      return t("errors.invalidInviteToken");
    } else {
      return t("errors.loginFailed");
    }
  };

  // ------------------------- Google Login -------------------------
  const googleLogin = useGoogleLogin({
    /*
            User authenticates with Google using their Google account. The user receives
            a temporary code in response from Google which is provided to our API login
            endpoint. On verification the backend returns a response containing an
            access token and refresh token.
             */
    onSuccess: (codeResponse) => {
      handleLogin("google", {
        code: codeResponse.code,
        invite_token: inviteToken,
      });
    },
    onError: (errorResponse) => {
      console.error("TODO: we need to log this", errorResponse);
    },
    flow: "auth-code",
    redirect_uri: import.meta.env.VITE_GOOGLE_AUTH_CALLBACK_URL,
  });

  // ------------------------- Microsoft Login ----------------------
  /*
        User authenticates with Microsoft using their Microsoft account. The user receives
        access token and id token in response from Microsoft which is provided to our API login
        endpoint. On verification the backend returns a response containing an
        access token and refresh token.
         */
  const { instance } = useMsal();
  const handleMicrosoftLogin = () => {
    instance
      .loginPopup(loginRequest)
      .then((res) => {
        handleLogin("microsoft", {
          access_token: res.accessToken,
          id_token: res.idToken,
          invite_token: inviteToken,
        });
      })
      .catch((e) => {
        console.error(e);
      });
  };

  // --------------------- Okta Login ----------------------------
  const handleOktaLogin = useOktaLogin({
    fetchLoginConfig,
    onSuccess: (code: string, state: string) =>
      handleLogin("okta", { code, state, invite_token: inviteToken }),
    onError: (err: unknown) => {
      console.error(err);
      setErrMsg(t("errors.loginFailed") || "");
    },
  });

  if (loadingAuth) {
    return <LoadingScreen active={true} />;
  }

  return (
    <div className="LoginPage">
      <div className="LoginPage__header">
        <Logo width={177} />
      </div>

      <div className="LoginPage__content">
        <div className="LoginPage__content__background">
          <img className="LoginPage__content__image" src={Login} alt="" />
        </div>
        <Card
          className="LoginPage__content__login"
          containerClassName="LoginPage__content__loginContainer"
        >
          <form className="LoginPage__content__login__form">
            <div
              className="LoginPage__content__heading"
              style={{ paddingBottom: "16px" }}
            >
              <Typography tagVariant="h1" desktop="h1" color="darkerStoneBlue">
                {t("signUp")}
              </Typography>
              <LanguagePicker onLoginPage />
            </div>
            <div
              className="LoginPage__content__login__form__google"
              style={{ marginBottom: "32px" }}
            >
              <p>{t("signUpDescription")}</p>
              <Button variant="contained" color="secondary">
                <a href="https://alda.co/book-a-demo">{t("signUpButton")}</a>
              </Button>
            </div>

            <div className="LoginPage__content__heading">
              <Typography tagVariant="h2" desktop="h1" color="darkerStoneBlue">
                {t("signIn")}
              </Typography>
            </div>
            {!tenantLoginId && (
              <>
                <div className="LoginPage__content__login__form__google">
                  <Button
                    variant="contained"
                    color="secondary"
                    onClick={() => googleLogin()}
                  >
                    <Icon type={IconTypes.GoogleG} />
                    {t("signInGoogle")}
                  </Button>
                </div>
                <div
                  className="LoginPage__content__login__form__microsoft"
                  style={{ marginTop: "5px" }}
                >
                  <Button
                    variant="contained"
                    color="secondary"
                    onClick={() => handleMicrosoftLogin()}
                  >
                    <Icon type={IconTypes.Microsoft} />
                    {t("signInMicrosoft")}
                  </Button>
                </div>
              </>
            )}
            {/* When a tenantLoginId is provided, assume the Okta login flow */}
            {tenantLoginId && (
              <div
                className="LoginPage__content__login__form__okta"
                style={{ marginTop: "5px" }}
              >
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={handleOktaLogin}
                  data-testid="okta-login-button"
                  loading={loadingLoginConfig && fetchStatus === "fetching"}
                >
                  <Icon type={IconTypes.Okta} />
                  {t("signInOkta")}
                </Button>
              </div>
            )}
            {errMsg && (
              <Paper
                showError
                className="LoginPage__content__login__form__error"
              >
                <WarningIcon />
                <Typography desktop="body2">{errMsg}</Typography>
              </Paper>
            )}
          </form>
          <Typography
            desktop="body2"
            color="secondary"
            className="LoginPage__content__help"
          >
            {t("userGuide.text")}
            <Link
              href="https://thisisalda.notion.site/The-Alda-Hub-17b9de1a53db80b5971ed208d798176f"
              openInNewTab
              variant="body2"
            >
              {t("userGuide.linkText")}
            </Link>
          </Typography>
        </Card>
      </div>
    </div>
  );
};
